import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, Truck } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { SupplierFormSheet } from "~/features/suppliers/supplier";
import { api, unwrap } from "~/lib/api";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";

const search = z.object({
  q: z.string().optional(),
  archived: z.enum(["exclude", "only"]).optional(),
  sort: z.enum(["name", "variantCount", "leadTimeDays"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  new: z.boolean().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/suppliers/")({ validateSearch: search, component: Suppliers });

function fetchSuppliers(params: Omit<Search, "new"> & { offset: number; limit: number }) {
  return unwrap(api.suppliers.get({ query: listQuery(params) }));
}
type Row = Awaited<ReturnType<typeof fetchSuppliers>>["items"][number];

function Suppliers() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const { new: creating, ...filters } = Route.useSearch();
  const list = useInfiniteList("suppliers", filters, fetchSuppliers);
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const columns: Column<Row>[] = [
    { id: "name", header: t("common.fields.name"), sortKey: "name", cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      id: "contact",
      header: t("suppliers.fields.contactName"),
      cell: (r) => r.contactName ?? "",
      className: "text-fg-2",
      minWidth: "lg",
    },
    {
      id: "country",
      header: t("common.fields.country"),
      cell: (r) => (r.country ? format.country(r.country) : ""),
      className: "text-fg-2",
    },
    { id: "currency", header: t("suppliers.fields.currency"), cell: (r) => r.currency, className: "text-fg-2", minWidth: "lg" },
    {
      id: "lead",
      header: t("suppliers.fields.leadTimeDays"),
      sortKey: "leadTimeDays",
      align: "end",
      cell: (r) => (r.leadTimeDays != null ? format.number(r.leadTimeDays) : ""),
      minWidth: "xl",
    },
    {
      id: "products",
      header: t("suppliers.fields.productCount"),
      sortKey: "variantCount",
      align: "end",
      cell: (r) => format.number(r.variantCount),
    },
  ];

  const newButton = can("suppliers:write") ? (
    <Button variant="primary" onClick={() => setSearch({ new: true })}>
      <Plus />
      {t("suppliers.new")}
    </Button>
  ) : null;

  return (
    <Page>
      <PageHeader
        title={t("suppliers.title")}
        meta={list.isLoading ? null : t("common.table.rows", { count: list.total })}
        actions={newButton}
      />
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("suppliers.searchPlaceholder")}
          />
          <Segmented
            label={t("common.fields.status")}
            value={filters.archived === "only" ? "archived" : "active"}
            onChange={(v) => setSearch({ archived: v === "archived" ? "only" : undefined })}
            options={[
              { value: "active", label: t("customers.all") },
              { value: "archived", label: t("customers.showArchived") },
            ]}
          />
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <>
                <p className="truncate font-medium">{r.name}</p>
                <p className="mt-0.5 truncate text-sm text-fg-2">
                  {[r.country ? format.country(r.country) : null, t("products.variantCount", { count: r.variantCount })]
                    .filter(Boolean)
                    .join(" — ")}
                </p>
              </>
            )}
            onOpen={(r) => void navigate({ to: "/suppliers/$id", params: { id: r.id } })}
            sort={{ key: filters.sort, dir: filters.dir ?? "asc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            isDimmed={(r) => Boolean(r.archivedAt)}
            empty={
              filters.q || filters.archived ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : (
                <EmptyState
                  icon={<Truck />}
                  title={t("suppliers.emptyTitle")}
                  body={t("suppliers.emptyBody")}
                  action={newButton}
                />
              )
            }
          />
        )}
      </Panel>
      <SupplierFormSheet
        open={Boolean(creating)}
        onOpenChange={(open) => setSearch({ new: open || undefined })}
        onSaved={(s) => void navigate({ to: "/suppliers/$id", params: { id: s.id } })}
      />
    </Page>
  );
}
